import React, { useEffect } from "react";
import { useLocation } from "wouter";
import { useCurrentUser } from "@/lib/auth";
import { Loader2 } from "lucide-react";

/**
 * Back-office role guard.
 *
 * Per monorepo convention (memory feedback_roles_lowercase) all roles live
 * lowercase in JWT (admin, econome, sedec_admin, …). The prototype used
 * UPPERCASE labels (ECOLE_ECONOME, DIOCESE_SEDEC_ADMIN, …). We accept
 * BOTH for back-compat and normalize on compare.
 */
const PROTO_TO_MONOREPO: Record<string, string> = {
  ECOLE_ECONOME: "econome",
  ECOLE_CAISSE: "caisse",
  DIOCESE_ECONOMAT: "econome_diocesain",
  DIOCESE_AUDITEUR: "auditeur_diocesain",
  DIOCESE_SEDEC_ADMIN: "sedec_admin",
  DIOCESE_SUPER_ADMIN: "super_admin",
};

function normalize(role: string | undefined | null): string {
  if (!role) return "";
  const upper = role.toUpperCase();
  if (PROTO_TO_MONOREPO[upper]) return PROTO_TO_MONOREPO[upper];
  return role.toLowerCase();
}

export default function RequireRole({
  roles,
  children,
}: {
  roles: string[];
  children: React.ReactNode;
}) {
  const { user, isAuthenticated, isLoading } = useCurrentUser();
  const [, setLocation] = useLocation();

  // Build the accepted set in lowercase canonical form.
  const accepted = new Set(roles.map(normalize));
  // L'auth-service renvoie roles[] (tableau). Accès autorisé si AU MOINS un
  // des rôles de l'utilisateur figure dans l'ensemble accepté.
  const userRoles = (user?.roles ?? []).map(normalize);
  const allowed = userRoles.some((r) => accepted.has(r));

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      setLocation("/auth/login");
      return;
    }
    // Authentifié mais hors périmètre → renvoyer vers l'espace parent par défaut.
    if (isAuthenticated && !allowed) {
      setLocation("/dashboard");
    }
  }, [isLoading, isAuthenticated, allowed, setLocation]);

  if (isLoading || !isAuthenticated || !allowed) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }
  return <>{children}</>;
}
